import { C } from '@ridgewill/brand';
import {
  adminApi,
  type AdminUser,
  type FamilyAttestation,
  type FamilyEvent,
  type FamilyMembership,
  type FamilyPerson,
} from '../lib/api.js';
import { useAsync } from '../lib/useAsync.js';
import { AgeBadge, isMinorBand } from '../components/AgeBand.js';
import { InfoBubble } from '../components/Screen.js';
import {
  AsyncBoundary,
  Avatar,
  Badge,
  DotPill,
  EmptyState,
  Panel,
  PanelBody,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { Fact, factsStyle } from './UserDetail.js';

/**
 * An account's Family Groups (org-admin#187, platform#54), READ-ONLY.
 *
 * Nothing here creates, changes or ends a membership: the members do that in
 * the account portal, and support has no lever on it. What staff need on a
 * call is all here: who is whose guardian, since when, the guardian's statement
 * behind agreements accepted for a member under 18, and what happened along
 * the way.
 *
 * ── EVERY GROUP, CURRENT FIRST, EACH ONCE ────────────────────────────
 *
 * A past membership still carries the statement behind agreements accepted
 * then, so ended ones are shown too, after the current one. A member who left
 * and rejoined the SAME group has two memberships of it: the group is shown
 * once, from the latest, its member table already lists both stints, and the
 * earlier statements sit under the current one. The tab is always present,
 * with an empty state, so a link to it never breaks.
 */
export function UserFamilyTab({ user }: { user: AdminUser }) {
  const { state } = useAsync(() => adminApi.userFamily(user.id), [user.id]);

  return (
    <AsyncBoundary state={state}>
      {(family) =>
        family.memberships.length === 0 ? (
          <Panel title="Family Group">
            <EmptyState
              title="Not in a Family Group"
              suggestion={
                isMinorBand(user.ageBand)
                  ? 'This account has never been in one. A member under 18 needs a guardian in a Family Group before anything that needs a guardian unlocks.'
                  : 'This account has never been in one.'
              }
            />
          </Panel>
        ) : (
          <>
            {byGroup(family.memberships).map((ms) => (
              <MembershipPanels key={ms[0]!.groupId} ms={ms} userId={user.id} />
            ))}
            <History events={family.history} userId={user.id} />
          </>
        )
      }
    </AsyncBoundary>
  );
}

/** Memberships grouped by group, keeping the server's order (current, then latest first). */
export function byGroup(memberships: FamilyMembership[]): FamilyMembership[][] {
  const out = new Map<string, FamilyMembership[]>();
  for (const m of memberships) out.set(m.groupId, [...(out.get(m.groupId) ?? []), m]);
  return [...out.values()];
}

function MembershipPanels({ ms, userId }: { ms: FamilyMembership[]; userId: string }) {
  const m = ms[0]!;
  const statements = ms.flatMap((x) => (x.attestation ? [x.attestation] : []));
  const current = m.leftAt === null;
  const self = m.group.members.find((x) => x.id === userId);
  const anyLeft = m.group.members.some((x) => x.leftAt);

  return (
    <>
      <Panel
        title={current ? 'Family Group' : 'Earlier Family Group'}
        info={
          <InfoBubble label="About this panel">
            Read-only. Members join, leave and remove each other themselves in the account portal; support
            cannot change a Family Group.
          </InfoBubble>
        }
        action={
          m.group.closedAt ? (
            <DotPill tone="muted">closed</DotPill>
          ) : current ? (
            <DotPill tone="good">open</DotPill>
          ) : (
            <DotPill tone="muted">{leftLabel(m.leftReason)}</DotPill>
          )
        }
      >
        <PanelBody>
          <div style={factsStyle}>
            <Fact label="Role">{roleLabel(m.role)}</Fact>
            {m.role === 'dependent' ? (
              <Fact label="Agreements">
                {m.adultAcknowledgedAt ? (
                  <>
                    Took over their own at 18, <When iso={m.adultAcknowledgedAt} />
                  </>
                ) : isMinorBand(self?.ageBand) ? (
                  'A guardian accepts too, until 18'
                ) : (
                  'Accepts their own'
                )}
              </Fact>
            ) : (
              <Fact label="Started the group">{m.startedTheGroup ? 'Yes' : 'No'}</Fact>
            )}
            <Fact label="In the group since">
              <When iso={m.joinedAt} />
            </Fact>
            {m.leftAt ? (
              <Fact label={leftLabel(m.leftReason)}>
                <When iso={m.leftAt} />
                {m.endedBy && m.endedBy.id !== userId ? (
                  <span style={{ color: C.muted }}> by {nameOf(m.endedBy)}</span>
                ) : null}
              </Fact>
            ) : m.role === 'guardian' ? (
              <Fact label="Open invitations">{m.group.openInvitations || 'None'}</Fact>
            ) : null}
            <Fact label="Group id">
              <code style={{ fontSize: '.82rem', color: C.textMid, wordBreak: 'break-all' }}>{m.group.id}</code>
            </Fact>
          </div>
          {m.group.closedAt ? (
            <p style={{ margin: 0, fontSize: '.86rem', color: C.textMid, lineHeight: 1.5 }}>
              This group closed <When iso={m.group.closedAt} />, when its last person left. It is kept because a
              member under 18 had agreements accepted for them, and goes when the retention purge removes its
              members&rsquo; records.
            </p>
          ) : null}
        </PanelBody>
        <Table columns={['Person', 'Role', 'Age', 'Since', ...(anyLeft ? ['Left'] : [])]}>
          {m.group.members.map((x) => (
            <tr key={`${x.id}-${x.joinedAt}`}>
              <Td>
                <PersonCell person={x} self={x.id === userId} />
              </Td>
              <Td>{x.role === 'guardian' ? <Badge tone="good">Guardian</Badge> : <Badge>Member</Badge>}</Td>
              <Td>
                <AgeBadge band={x.ageBand} />
              </Td>
              <Td>
                <When iso={x.joinedAt} />
              </Td>
              {anyLeft ? (
                <Td>
                  {x.leftAt ? (
                    <span title={leftLabel(x.leftReason)}>
                      <When iso={x.leftAt} />
                    </span>
                  ) : (
                    <span style={{ color: C.muted }}>&ndash;</span>
                  )}
                </Td>
              ) : null}
            </tr>
          ))}
        </Table>
      </Panel>

      {statements.length > 0 ? <Statement a={statements[0]!} earlier={statements.slice(1)} /> : null}
    </>
  );
}

/**
 * The guardian's attestation behind agreements accepted for this member, with
 * where it was given. Legal evidence, so every recorded field is shown.
 */
function Statement({ a, earlier }: { a: FamilyAttestation; earlier: FamilyAttestation[] }) {
  const place = [a.city, a.region, a.country].filter(Boolean).join(', ');
  return (
    <Panel
      title="Guardian's statement"
      info={
        <InfoBubble label="About the statement">
          Given when the invitation was sent, and copied onto the membership when the member joined. It is the
          evidence behind every agreement a guardian accepts for them.
        </InfoBubble>
      }
    >
      <PanelBody>
        <figure style={s.statement}>
          <q style={{ fontSize: '.95rem', fontWeight: 600 }}>I am this person&rsquo;s parent or legal guardian.</q>
          {a.statedUnder18 !== null ? (
            <span style={{ fontSize: '.82rem', color: C.muted }}>
              Answered &ldquo;Is this person under 18?&rdquo; with {a.statedUnder18 ? 'yes' : 'no'}, then &ldquo;Are you
              their parent or legal guardian?&rdquo; with yes.
            </span>
          ) : null}
        </figure>
        <div style={factsStyle}>
          <Fact label="Given by">
            {a.by ? <PersonLink person={a.by} /> : <span style={{ color: C.muted }}>a deleted account</span>}
          </Fact>
          <Fact label="When">
            <When iso={a.at} />
          </Fact>
          <Fact label="Place">
            {place ? (
              <>
                {place} <span style={{ color: C.muted }}>by IP</span>
              </>
            ) : (
              <span style={{ color: C.muted }}>not recorded</span>
            )}
          </Fact>
          <Fact label="IP address">
            {a.ip ? <code style={s.code}>{a.ip}</code> : <span style={{ color: C.muted }}>not recorded</span>}
          </Fact>
        </div>
        {a.userAgent ? (
          <div style={factsStyle}>
            <Fact label="Browser">
              <code style={s.code}>{a.userAgent}</code>
            </Fact>
          </div>
        ) : null}
      </PanelBody>
      {earlier.length > 0 ? (
        <>
          <PanelBody>
            <span style={{ fontSize: '.82rem', color: C.muted }}>
              Earlier statements, from previous times this member was in the group
            </span>
          </PanelBody>
          <Table columns={['When', 'Given by', 'Place', 'IP address']}>
            {earlier.map((e) => (
              <tr key={e.at}>
                <Td>
                  <When iso={e.at} />
                </Td>
                <Td>{e.by ? <PersonLink person={e.by} /> : <span style={{ color: C.muted }}>a deleted account</span>}</Td>
                <Td>{[e.city, e.region, e.country].filter(Boolean).join(', ') || <span style={{ color: C.muted }}>not recorded</span>}</Td>
                <Td mono>{e.ip ?? '–'}</Td>
              </tr>
            ))}
          </Table>
        </>
      ) : null}
    </Panel>
  );
}

function History({ events, userId }: { events: FamilyEvent[]; userId: string }) {
  if (events.length === 0) return null;
  return (
    <Panel title="History" action={<span style={{ fontSize: '.82rem', color: C.muted }}>Newest first</span>}>
      <Table columns={['When', 'What happened', 'By']}>
        {events.map((e, i) => (
          <tr key={`${e.at}-${e.kind}-${i}`}>
            <Td>
              <When iso={e.at} />
            </Td>
            <Td>{describeEvent(e, userId)}</Td>
            <Td>{e.by ? <PersonCell person={e.by} compact /> : <span style={{ color: C.muted }}>&ndash;</span>}</Td>
          </tr>
        ))}
      </Table>
    </Panel>
  );
}

/** One line per event, in plain words. Exported for the tests. */
export function describeEvent(e: FamilyEvent, userId: string): string {
  const them = e.subject ? nameOf(e.subject) : 'someone';
  const sentAs =
    e.statedUnder18 === true ? 'as under 18' : e.statedUnder18 === false ? 'as 18 or older' : null;
  switch (e.kind) {
    case 'joined':
      return 'Joined the group';
    case 'left':
      return 'Left the group';
    case 'removed':
      return e.subject && e.subject.id !== userId ? `Removed ${them} from the group` : 'Removed from the group';
    case 'closed-account':
      return 'Left the group when the account closed';
    case 'invitation-declined':
      return `Declined an invitation from ${them}`;
    case 'invitation-refused-minor':
      return `Refused: ${them}'s invitation was sent as 18 or older, and this account is under 18`;
    case 'invitation-refused-adult':
      return `Refused: ${them}'s invitation was for someone under 18, and this account is 18 or older`;
    case 'invited': {
      const outcome =
        e.outcome === 'accepted'
          ? 'joined'
          : e.outcome === 'declined'
            ? 'declined'
            : e.outcome === 'refused_minor'
              ? 'refused, because they are under 18'
              : e.outcome === 'refused_adult'
                ? 'refused, because they are 18 or older'
                : (e.outcome ?? 'decided');
      return `Invited ${them}${sentAs ? ` ${sentAs}` : ''}: ${outcome}`;
    }
    default:
      return e.kind;
  }
}

function PersonCell({ person, self, compact }: { person: FamilyPerson; self?: boolean; compact?: boolean }) {
  const label = person.name || person.email || 'Closed account';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '.55rem', minWidth: 0 }}>
      <Avatar id={person.id} label={label} image={person.image} size={26} />
      {compact ? (
        <PersonLink person={person} />
      ) : (
        <span style={{ display: 'grid', minWidth: 0 }}>
          <PersonLink person={person} />
          <span style={{ fontSize: '.8rem', color: C.muted }}>
            {[person.name && person.email ? person.name : null, self ? 'this account' : null]
              .filter(Boolean)
              .join(', ')}
          </span>
        </span>
      )}
    </div>
  );
}

function PersonLink({ person }: { person: FamilyPerson }) {
  return (
    <a href={`#/users/${encodeURIComponent(person.id)}/family`} style={s.personLink}>
      {person.email ?? person.name ?? 'Closed account'}
    </a>
  );
}

const roleLabel = (role: string) => (role === 'guardian' ? 'Guardian' : 'Member');

function leftLabel(reason: string | null): string {
  if (reason === 'removed') return 'Removed';
  if (reason === 'closed') return 'Account closed';
  return 'Left';
}

function nameOf(p: FamilyPerson): string {
  return p.name || p.email || 'a closed account';
}

const s = {
  statement: {
    margin: 0,
    padding: '.9rem 1rem',
    borderRadius: 8,
    background: C.page,
    border: `1px solid ${C.border}`,
    display: 'grid',
    gap: '.35rem',
  },
  code: { fontSize: '.82rem', color: C.textMid, wordBreak: 'break-all' as const },
  personLink: { color: C.text, fontWeight: 600, textDecoration: 'none' },
};
